import { useState, useEffect, useMemo } from 'react';
import { useRef } from 'react';
// Remove AgGrid imports
// import { AgGridReact } from 'ag-grid-react';
// import { ModuleRegistry, AllCommunityModule, ColDef } from 'ag-grid-community';
// Add KendoReact Grid imports
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ChartProps } from '../Interfaces/Charts';
import { Button } from 'react-bootstrap';
import { Download } from '../Utils/Downloader';
import VCenteredModal from '../Components/VCenteredModal';
import { DESCRIPTION_MESSAGE, DOWNLOAD_MESSAGE, POPOUT_MESSAGE, SUBREGIONAL_POWER_BALANCE_BINDING_CONSTRAINTS_SUFFIX } from '../Constants';

// Remove ModuleRegistry line
// ModuleRegistry.registerModules([AllCommunityModule]);

export default function SubRegionalPowerBalanceBindingConstraints(props: ChartProps) {
    // ***************************************************************************************************************************************************
    // *** Custom Block: the following two defintions are custom to each table this code can produce,
    //      they provide the location for the data and the names of the columns to display
    const api_url = `${process.env.REACT_APP_PUBLIC_API_URL}${SUBREGIONAL_POWER_BALANCE_BINDING_CONSTRAINTS_SUFFIX}`;
    const download_filename = 'SubRegionalPowerBalanceBindingConstraints';
    const popout_path = '/charts/subregionalpowerbalancebindingconstraints';
    const modalHeader: React.ReactNode = <span>Sub-Regional Power Balance Binding Constraints</span>;

    interface IRow {
        Name: string;
        Price: number | string;
    }

    // Remove defaultColDef - not needed for KendoReact Grid
    // const defaultColDef = useMemo<ColDef>(() => {
    //	return {
    //		resizable: true,
    //		wrapHeaderText: true,
    //		autoHeaderHeight: true
    //	};
    // }, []);

    // Remove columnDefs - we'll use Column components instead
    // const [columnDefs] = useState<any[]>([...]);

    function extractRowDataAndIntervalString(json: any) {
        const rowData = json.Constraint ?? [];
        const intervalString = json.RefId ?? '';
        return { rowData, intervalString };
    }

    const descriptionBody: React.ReactNode = useMemo((): React.ReactNode => {
        return (
            <>
                <span>
                    The Real-Time Binding Sub-Regional Power Balance Constraints table shows the Shadow Price associated with limiting the dispatch transfer between the MISO South
                    Region and the rest of MISO within the agreed to administrative values. The table lists currently active constraints of this type, reflecting where
                    internal balancing limitations are actively influencing market dispatch and pricing.
                </span>
                <br />
                <br /> <span>This table is updated every 5 minutes.</span>
            </>
        );
    }, []);
    // ***************************************************************************************************************************************************

    const [buttonClicked] = [props.buttonClicked];
    const [showModal, setShowModal] = useState(false);
    const [modalBody, setModalBody] = useState<React.ReactNode>(<></>);
    const [rawData, setRawData] = useState('');

    useEffect(() => {
        let downloadBody: React.ReactNode = (
            <div className='download-modal'>
                <Button className='download-btn' onClick={() => Download(api_url, download_filename, 'json')}>
                    Download JSON
                </Button>
            </div>
        );

        switch (buttonClicked) {
            case DESCRIPTION_MESSAGE:
                setModalBody(descriptionBody);
                setShowModal(true);
                break;
            case DOWNLOAD_MESSAGE:
                setModalBody(downloadBody);
                setShowModal(true);
                break;
            case POPOUT_MESSAGE:
                const newWinddow = window.open(popout_path, '_blank', 'width=800,height=600,noopener,noreferrer');
                if (newWinddow) newWinddow.opener = null;
                break;
            default:
                break;
        }
    }, [buttonClicked, rawData, api_url, descriptionBody]);

    const REFRESH_MIN_MS = 290_000;
    const REFRESH_MAX_MS = 310_000;
    const refreshIntervalRef = useRef<number>(
        Math.floor(REFRESH_MIN_MS + Math.random() * (REFRESH_MAX_MS - REFRESH_MIN_MS))
    );
    // console.debug('SubRegionalPowerBalance refresh (ms):', refreshIntervalRef.current);

    const useFetchJson = <T,>(url: string) => {
        const [data, setData] = useState<T[]>();
        const [loading, setLoading] = useState(false);
        const [dataInterval, setDataInterval] = useState<string>('');
        const [error, setError] = useState<string>('');

        useEffect(() => {
            let intervalId: NodeJS.Timeout;
            const fetchData = async () => {
                setLoading(true);
                setError(''); // Reset error state before new fetch
                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error();
                    const json = await response.json();
                    setRawData(json);
                    const { rowData, intervalString } = extractRowDataAndIntervalString(json);
                    setDataInterval(intervalString);
                    // Coerce numeric Price for proper numeric sorting
                    const coerced = rowData.map((r: any) => {
                        const c: any = { ...r };
                        if (c.Price !== undefined && c.Price !== null) {
                            const num = parseFloat(String(c.Price).replace(/,/g, ''));
                            if (!Number.isNaN(num)) c.Price = num;
                        }
                        return c;
                    });
                    setData(coerced);
                } catch (error) {
                    setData([]); // On error, set data to empty array
                    setError('Data Connection Failed'); // Set error message
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
            intervalId = setInterval(fetchData, refreshIntervalRef.current);
            return () => clearInterval(intervalId);
        }, [url]);

        return { data, loading, dataInterval, error };
    };

    const { data, loading, dataInterval, error } = useFetchJson<IRow>(api_url);

    // Controlled sorting
    const [sort, setSort] = useState<SortDescriptor[]>([]);
    const sortedData = useMemo(
        () => orderBy(data || [], sort),
        [data, sort]
    );

    if (loading) {
        return <div className='loading-and-error-box'>Loading...</div>;
    }
    if (error) {
        return <div className='loading-and-error-box'>{error}</div>;
    }
    if (!data || data.length === 0) {
        return <div className='loading-and-error-box'>No Data Available</div>;
    }

    return (
        <>
            <VCenteredModal show={showModal} onHide={() => setShowModal(false)} headercontent={modalHeader} bodycontent={modalBody} />
            <div className='interval-line'>{dataInterval}</div>
            <div className='full-width-grid-wrapper'>
                <Grid
                    data={sortedData}
                    style={{ width: '100%' }}
                    scrollable="none"
                    resizable
                    sortable
                    sort={sort}
                    onSortChange={e => setSort(e.sort)}
                >
                    <Column field="Name" title="Name"  />
                    <Column field="Price" title="Price" className="k-text-end" />
                </Grid>
            </div>
        </>
    );
}
